import type { ApiErrorBody } from './types'

const CODE_MESSAGES: Record<string, string> = {
  bad_request: 'Сервер отклонил запрос',
  not_found: 'Объект не найден — возможно, он уже удалён',
  unsupported_media: 'Неподдерживаемый формат файла',
  too_large: 'Файл слишком большой',
  ffmpeg_missing: 'На сервере не найден ffmpeg — рендер недоступен',
  analyzer_unavailable:
    'Сервис автоматического разбора не отвечает. Он тяжёлый и запускается отдельно — см. analyzer/README.md',
  analyzer_busy: 'Разбор уже выполняется — одновременно идёт только одна задача',
  unauthorized: 'Нужен вход администратора',
  forbidden: 'Недостаточно прав',
  conflict: 'Действие невозможно в текущем состоянии',
  internal: 'Внутренняя ошибка сервера',
  network: 'Сервер недоступен. Проверьте, запущен ли бэкенд на :8080',
  timeout: 'Сервер не ответил вовремя',
  aborted: 'Операция отменена',
  parse: 'Сервер вернул неожиданный ответ',
}

/** Ошибка обращения к API: и HTTP-ошибки, и недоступность сети. */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  /** true, если сервер вообще не ответил — повод показать баннер «сервер недоступен». */
  readonly offline: boolean

  constructor(code: string, message: string, status = 0) {
    super(message || CODE_MESSAGES[code] || 'Ошибка')
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.offline = code === 'network' || code === 'timeout'
  }

  /** Разбирает тело ответа сервера в ошибку, не падая на не-JSON. */
  static fromBody(status: number, body: string): ApiError {
    try {
      const parsed: unknown = JSON.parse(body)
      if (isApiErrorBody(parsed)) {
        return new ApiError(parsed.error.code, parsed.error.message ?? '', status)
      }
    } catch {
      // Не JSON — падать на этом нельзя, текст ошибки всё равно нужен.
    }
    return new ApiError(status === 0 ? 'network' : 'internal', body.slice(0, 200), status)
  }

  /** Человекочитаемый текст для показа пользователю. */
  get userMessage(): string {
    const base = CODE_MESSAGES[this.code] ?? 'Ошибка запроса к серверу'
    const detail = this.message && this.message !== base ? this.message : ''
    return detail ? `${base}: ${detail}` : base
  }
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false
  const err = (value as { error?: unknown }).error
  if (typeof err !== 'object' || err === null) return false
  return typeof (err as { code?: unknown }).code === 'string'
}

/** Приводит что угодно из catch к тексту на русском. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) return err.userMessage
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
        return 'Доступ к микрофону запрещён. Разрешите его в настройках браузера.'
      case 'NotFoundError':
        return 'Микрофон не найден. Подключите устройство записи.'
      case 'NotReadableError':
        return 'Микрофон занят другим приложением.'
      case 'AbortError':
        return 'Операция прервана'
      default:
        return err.message || err.name
    }
  }
  if (err instanceof Error) return err.message
  return String(err)
}
